import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Upload, FileDown, AlertTriangle, CheckCircle2, X, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { parseSbsImport, downloadSbsTemplate, downloadBikeSampleSbs } from "../lib/sbsImport";
import { useEstimate } from "../store/EstimateContext";

export default function SbsImporter() {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [mode, setMode] = useState("replace");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [warningsOpen, setWarningsOpen] = useState(true);
  const { applyImportedSbs } = useEstimate();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const parsed = await parseSbsImport(file);
      applyImportedSbs(parsed, mode);
      setResult({
        ok: true, mode, filename: file.name,
        count: parsed.count,
        warnings: parsed.warnings || [],
        stats: parsed.stats || null,
      });
      setWarningsOpen(true);
      const wcount = (parsed.warnings || []).length;
      toast.success(t("sbsImporter.successToast"), {
        description: wcount > 0
          ? `${parsed.count} ${t("sbsImporter.systems")} · ${wcount} ${t("sbsImporter.warningsToast")}`
          : `${parsed.count} ${t("sbsImporter.systems")} · ${mode}`,
      });
    } catch (err) {
      setResult({ ok: false, filename: file.name, error: String(err.message || err) });
      toast.error(t("sbsImporter.failureToast"), { description: String(err.message || err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-gray-300 p-4" data-testid="sbs-importer">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Upload size={14} className="text-[#002FA7]" />
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              {t("sbsImporter.label")}
            </p>
          </div>
          <h3 className="font-heading text-base tracking-tight font-bold text-black">
            {t("sbsImporter.title")}
          </h3>
          <p className="text-xs text-gray-600 mt-1 max-w-xl">
            {t("sbsImporter.description")}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex border border-gray-300" data-testid="sbs-import-mode-toggle">
            <button type="button" onClick={() => setMode("replace")}
              data-testid="sbs-import-mode-replace"
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${mode === "replace" ? "bg-[#002FA7] text-white" : "bg-white text-gray-700 hover:bg-gray-100"}`}>
              {t("importer.modeReplace")}
            </button>
            <button type="button" onClick={() => setMode("merge")}
              data-testid="sbs-import-mode-merge"
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-l border-gray-300 ${mode === "merge" ? "bg-[#002FA7] text-white" : "bg-white text-gray-700 hover:bg-gray-100"}`}>
              {t("importer.modeMerge")}
            </button>
          </div>
          <button type="button" onClick={downloadSbsTemplate}
            data-testid="sbs-import-template-btn"
            className="bg-white border border-gray-300 hover:bg-gray-100 rounded-none px-3 py-1.5 font-bold text-xs inline-flex items-center gap-1">
            <FileDown size={12} /> {t("importer.templateBtn")}
          </button>
          <button type="button" onClick={downloadBikeSampleSbs}
            data-testid="sbs-import-bike-sample-btn"
            title="Download a populated S1000D Bike sample workbook"
            className="bg-white border border-gray-300 hover:bg-gray-100 rounded-none px-3 py-1.5 font-bold text-xs inline-flex items-center gap-1">
            <FileDown size={12} /> Bike sample
          </button>
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
            data-testid="sbs-import-btn"
            className="bg-[#002FA7] text-white hover:bg-black rounded-none px-3 py-1.5 font-bold text-xs inline-flex items-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed">
            {busy ? <><Loader2 size={12} className="animate-spin" /> {t("importer.importingBtn")}</> : <><Upload size={12} /> {t("importer.importBtn")}</>}
          </button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls"
            onChange={handleFile} data-testid="sbs-import-file-input" className="hidden" />
        </div>
      </div>
      {result && (
        <div className="mt-3 border-t border-gray-200 pt-3" data-testid="sbs-import-result">
          {result.ok ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs flex-wrap">
                <CheckCircle2 size={14} className="text-[#15803D]" />
                <span className="font-bold">{result.filename}</span>
                <span className="text-gray-500 font-mono">· {result.count} {t("sbsImporter.systems")} · {result.mode}</span>
                {result.warnings.length > 0 && (
                  <span className="ml-1 inline-flex items-center gap-1 bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                    data-testid="sbs-import-warning-badge">
                    <AlertTriangle size={10} /> {result.warnings.length} {t("sbsImporter.warningsLabel")}
                  </span>
                )}
              </div>
              {result.stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono"
                  data-testid="sbs-import-stats">
                  <div className="border border-gray-200 px-2 py-1.5">
                    <p className="text-[9px] font-bold uppercase tracking-wider text-gray-500">{t("sbsImporter.stats.applied")}</p>
                    <p className="text-sm font-bold" data-testid="sbs-import-stats-applied">{result.stats.rowsApplied}</p>
                  </div>
                  <div className="border border-gray-200 px-2 py-1.5">
                    <p className="text-[9px] font-bold uppercase tracking-wider text-gray-500">{t("sbsImporter.stats.systems")}</p>
                    <p className="text-sm font-bold" data-testid="sbs-import-stats-systems">{result.stats.systemCount}</p>
                  </div>
                  <div className="border border-gray-200 px-2 py-1.5">
                    <p className="text-[9px] font-bold uppercase tracking-wider text-gray-500">{t("sbsImporter.stats.skipped")}</p>
                    <p className="text-sm font-bold" data-testid="sbs-import-stats-skipped">{result.stats.rowsSkipped}</p>
                  </div>
                  <div className="border border-gray-200 px-2 py-1.5">
                    <p className="text-[9px] font-bold uppercase tracking-wider text-gray-500">{t("sbsImporter.stats.zeroQty")}</p>
                    <p className="text-sm font-bold" data-testid="sbs-import-stats-zero">{result.stats.rowsZeroQty}</p>
                  </div>
                </div>
              )}
              {result.warnings.length > 0 && (
                <div className="bg-[#FFFBEB] border-l-4 border-[#CA8A04] px-3 py-2"
                  data-testid="sbs-import-warnings">
                  <button type="button"
                    onClick={() => setWarningsOpen((v) => !v)}
                    data-testid="sbs-import-warnings-toggle"
                    className="w-full flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#92400E]">
                    {warningsOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    <AlertTriangle size={12} />
                    {t("sbsImporter.warningsLabel")} ({result.warnings.length})
                  </button>
                  {warningsOpen && (
                    <ul className="mt-2 space-y-1 text-[11px] text-[#78350F] font-mono leading-snug">
                      {result.warnings.map((w, i) => (
                        <li key={i} className="flex items-start gap-1.5"
                          data-testid={`sbs-import-warning-${i}`}>
                          <span className="text-[#CA8A04] shrink-0">•</span>
                          <span>{w}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-[#FF2400]">
              <X size={14} /> <span className="font-bold">{result.filename}</span>
              <span className="font-mono">{result.error}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
